'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useAui, useAuiState } from '@assistant-ui/react';
import { useAISDKChat } from '@assistant-ui/react-ai-sdk';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { useToasterId } from '@/components/toaster-context';
import { PiiWarningDialog } from '@/components/ui/pii-warning-dialog';
import { useTrackEvent } from '@/hooks/use-track-event';
import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { usePiiResultChannel, type PiiVerdict } from '@/lib/pii/pii-result-channel';
import {
  maskedMessagesOf,
  PII_TYPE_LABEL,
  piiEventProperties,
  PiiRefusalError,
  type PiiBlocked,
  type PiiDecision,
  type PiiItem,
  type PiiMasked,
  type PiiNeedsConfirmation,
} from '@/lib/pii/server-pii';
import { dropRefusedMessage, withMaskedText } from '@/lib/pii/thread-edits';
import { markChecked } from '@/lib/pii/unchecked-messages';
import { bidiIsolated } from '@/lib/utils/rtl';

const BLOCKED_TOAST_DURATION_MS = 5000;
const MASKED_TOAST_DURATION_MS = 8000;

type OpenWarning = {
  items: PiiItem[];
  /** Only what the server checked: text typed while the turn was on the wire was never checked. */
  warnedText: string;
  threadId: string;
  /** Fixed when the dialog opens, so `pii_warning_decided` repeats `pii_warning_shown` exactly. */
  eventProperties: ReturnType<typeof piiEventProperties>;
};

export function PiiResultHandler() {
  const t = useTranslations();
  const toasterId = useToasterId();
  const trackEvent = useTrackEvent();
  const aui = useAui();
  const chat = useAISDKChat();
  // A mask waits for its message's chat to be on screen: a thread's first turn is sent before its
  // chat mounts, and the guest may switch threads before the answer.
  const pendingMasks = useRef(new Map<string, string>());
  // Read through a ref: the chat object changes on every streamed chunk, and the listener below
  // would otherwise be rebuilt and re-registered with it.
  const chatRef = useRef(chat);
  const channelRef = usePiiResultChannel();
  const [warning, setWarning] = useState<OpenWarning | null>(null);
  const mainThreadId = useAuiState(state => state.threads.mainThreadId);

  // The message stays unchecked until here, so history cannot store the raw text before this.
  const applyPendingMasks = useCallback((currentChat: NonNullable<typeof chat>) => {
    for (const [messageId, text] of pendingMasks.current) {
      if (currentChat.messages.some(message => message.id === messageId)) {
        pendingMasks.current.delete(messageId);
        currentChat.setMessages(messages => withMaskedText({ messages, messageId, text }));
        markChecked([messageId]);
      }
    }
  }, []);

  const restoreToComposer = useCallback(
    (messageId: string | undefined) => {
      const droppedText = dropRefusedMessage({ thread: aui.thread, messageId });
      if (droppedText !== null) {
        const typedSince = aui.thread.composer().getState().text.trim();
        aui.thread.composer().setText(typedSince ? `${droppedText}\n${typedSince}` : droppedText);
      }
      return droppedText;
    },
    [aui],
  );

  const handleBlocked = useCallback(
    ({
      result: {
        items: [item],
      },
      messageId,
    }: PiiVerdict<PiiBlocked>) => {
      const droppedText = restoreToComposer(messageId);
      const properties = piiEventProperties({ item, messageLength: droppedText?.length });
      trackEvent({ event: CHAT_ANALYTICS_EVENTS.PII_BLOCK, properties });
      toast.error(
        t('sensitiveData.warning', {
          type: t(`sensitiveData.types.${properties.category}`),
          value: bidiIsolated(item.masked),
        }),
        { duration: BLOCKED_TOAST_DURATION_MS, toasterId },
      );
    },
    [restoreToComposer, t, toasterId, trackEvent],
  );

  const handleNeedsConfirmation = useCallback(
    ({ result, messageId }: PiiVerdict<PiiNeedsConfirmation>) => {
      const droppedText = restoreToComposer(messageId);
      // After a thread switch the message stays where it was, so there is nothing to confirm here.
      if (!droppedText) {
        return;
      }
      const eventProperties = piiEventProperties({
        item: result.items[0],
        messageLength: droppedText.length,
        itemCount: result.items.length,
      });
      trackEvent({ event: CHAT_ANALYTICS_EVENTS.PII_WARNING_SHOWN, properties: eventProperties });
      setWarning({
        items: result.items,
        warnedText: droppedText,
        threadId: aui.threads.getState().mainThreadId,
        eventProperties,
      });
    },
    [aui, restoreToComposer, trackEvent],
  );

  const decideWarning = useCallback(
    (choice: PiiDecision | 'edit') => {
      if (!warning) {
        return;
      }
      setWarning(null);
      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.PII_WARNING_DECIDED,
        properties: { ...warning.eventProperties, choice },
      });
      const currentChat = chatRef.current;
      if (choice === 'edit' || !currentChat) {
        return;
      }
      const composer = aui.thread.composer();
      const composerText = composer.getState().text;
      composer.setText(
        composerText.startsWith(warning.warnedText)
          ? composerText.slice(warning.warnedText.length).trim()
          : composerText,
      );
      // The decision rides on the message, so every later turn that resends it carries it too.
      void currentChat.sendMessage({
        text: warning.warnedText,
        metadata: { piiDecision: choice },
      });
    },
    [aui, trackEvent, warning],
  );

  const handleMasked = useCallback(
    ({ result, messageId }: PiiVerdict<PiiMasked>) => {
      for (const { id, text } of maskedMessagesOf({ result, newestMessageId: messageId })) {
        pendingMasks.current.set(id, text);
      }
      const [item] = result.items;
      if (chatRef.current) {
        applyPendingMasks(chatRef.current);
      }
      toast.info(
        t('sensitiveData.masked', {
          type: t(`sensitiveData.types.${PII_TYPE_LABEL[item.type]}`),
          typeKey: PII_TYPE_LABEL[item.type],
        }),
        { duration: MASKED_TOAST_DURATION_MS, toasterId },
      );
    },
    [applyPendingMasks, t, toasterId],
  );

  // The chat records the block as its error after onError returns, which would mark the last
  // message on screen as failed; a layout effect clears it before that is painted.
  useLayoutEffect(() => {
    if (chat?.error instanceof PiiRefusalError) {
      chat.clearError();
    }
  }, [chat]);

  useEffect(() => {
    chatRef.current = chat;
    if (chat) {
      applyPendingMasks(chat);
    }
  }, [applyPendingMasks, chat]);

  useEffect(() => {
    if (!channelRef) {
      return;
    }
    channelRef.current = function dispatchPiiVerdict({ result, messageId }: PiiVerdict) {
      switch (result.result) {
        case 'blocked':
          return handleBlocked({ result, messageId });
        case 'needs_confirmation':
          return handleNeedsConfirmation({ result, messageId });
        default:
          return handleMasked({ result, messageId });
      }
    };
    return () => {
      channelRef.current = null;
    };
  }, [channelRef, handleBlocked, handleMasked, handleNeedsConfirmation]);

  // The warned text waits in its own thread's composer, so the dialog shows only over that thread.
  return (
    warning?.threadId === mainThreadId && (
      <PiiWarningDialog items={warning.items} onDecide={decideWarning} />
    )
  );
}
